const URL_API_KEYS = [
  "countryId",
  "countrySlug",
  "cityId",
  "citySlug",
  "keywords",
  "activityTypeIds",
  "subTypeIds",
  "cityIds",
  "activityIds",
];

const NUMERIC_URL_KEYS = new Set(["countryId", "cityId"]);

const ARRAY_URL_KEYS = new Set([
  "activityTypeIds",
  "subTypeIds",
  "cityIds",
  "activityIds",
]);

const QUERY_KEY_MAP = {
  countryid: "countryId",
  countryslug: "countrySlug",
  countryname: "countryName",
  cityid: "cityId",
  cityslug: "citySlug",
  cityname: "cityName",
  keywords: "keywords",
  keyword: "keywords",
  activitytypeid: "activityTypeIds",
  activitytypeids: "activityTypeIds",
  subtypeid: "subTypeIds",
  subtypeids: "subTypeIds",
  activityid: "activityIds",
  activityids: "activityIds",
};

function normalizeQueryKey(key = "") {
  return String(key).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

function slugifyValue(value = "") {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function parseIdList(raw) {
  if (raw == null || raw === "") return [];

  const values = Array.isArray(raw) ? raw : String(raw).split(",");
  return values.map((item) => Number(item)).filter((item) => item > 0);
}

function parseStringValue(raw) {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const text = String(value ?? "").trim();
  return text || "";
}

function setParam(params, key, value) {
  if (value == null) return;
  if (typeof value === "string" && value.trim() === "") return;
  if (Array.isArray(value) && !value.length) return;
  params[key] = value;
}

function mergeIdList(params, key, raw) {
  const next = parseIdList(raw);
  if (!next.length) return;
  params[key] = [...new Set([...(params[key] || []), ...next])];
}

export function compactActivityListPayload(payload = {}) {
  const result = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (value == null) return;

    if (typeof value === "string" && value.trim() === "") return;

    if (typeof value === "number") {
      if (value === 0) return;
      result[key] = value;
      return;
    }

    if (typeof value === "boolean") {
      if (!value) return;
      result[key] = value;
      return;
    }

    if (Array.isArray(value)) {
      const cleaned = value.filter(
        (item) =>
          item != null &&
          item !== "" &&
          !(typeof item === "number" && item === 0)
      );
      if (!cleaned.length) return;
      result[key] = cleaned;
      return;
    }

    result[key] = value;
  });

  return result;
}

export function parseUrlListParams(query = {}) {
  const params = {};

  Object.entries(query || {}).forEach(([rawKey, rawValue]) => {
    const mappedKey = QUERY_KEY_MAP[normalizeQueryKey(rawKey)];
    if (!mappedKey) return;

    if (mappedKey === "countryName") {
      const name = parseStringValue(rawValue);
      if (!name) return;
      params.countryName = name;
      if (!params.countrySlug) {
        setParam(params, "countrySlug", slugifyValue(name));
      }
      return;
    }

    if (mappedKey === "cityName") {
      const name = parseStringValue(rawValue);
      if (!name) return;
      params.cityName = name;
      if (!params.citySlug) {
        setParam(params, "citySlug", slugifyValue(name));
      }
      return;
    }

    if (NUMERIC_URL_KEYS.has(mappedKey)) {
      const parsed = Number(parseStringValue(rawValue));
      if (parsed > 0) params[mappedKey] = parsed;
      return;
    }

    if (ARRAY_URL_KEYS.has(mappedKey)) {
      mergeIdList(params, mappedKey, rawValue);
      return;
    }

    setParam(params, mappedKey, parseStringValue(rawValue));
  });

  if (params.cityId && !params.cityIds?.length) {
    params.cityIds = [params.cityId];
  }

  return params;
}

export function pickUrlApiParams(urlParams = {}) {
  const result = {};

  URL_API_KEYS.forEach((key) => {
    if (urlParams[key] == null) return;
    result[key] = urlParams[key];
  });

  if (urlParams.cityId && !result.cityIds?.length) {
    result.cityIds = [urlParams.cityId];
  }

  return result;
}

const FILTER_QUERY_MAP = {
  countryid: { apiKey: "countryId", type: "number" },
  cityslug: { apiKey: "citySlug", type: "string" },
  activitytypeslug: { apiKey: "activityTypeSlug", type: "string" },
  activitysubtypeslug: { apiKey: "activitySubTypeSlug", type: "string" },
  keywords: { apiKey: "searchText", type: "string" },
  keyword: { apiKey: "searchText", type: "string" },
  activityids: { apiKey: "activityIds", type: "numberArray" },
  activityid: { apiKey: "activityIds", type: "numberArray" },
  pagesize: { apiKey: "pageSize", type: "number" },
  pageindex: { apiKey: "pageIndex", type: "number" },
  activitytypeids: { apiKey: "activityTypeIds", type: "numberArray" },
  activitytypeid: { apiKey: "activityTypeIds", type: "numberArray" },
  subtypeids: { apiKey: "subTypeIds", type: "numberArray" },
  subtypeid: { apiKey: "subTypeIds", type: "numberArray" },
  cityids: { apiKey: "cityIds", type: "numberArray" },
  cityid: { apiKey: "cityIds", type: "numberArray" },
  timeofday: { apiKey: "timeOfDay", type: "stringArray" },
  durations: { apiKey: "durations", type: "stringArray" },
  duration: { apiKey: "durations", type: "stringArray" },
  minprice: { apiKey: "minPrice", type: "number" },
  maxprice: { apiKey: "maxPrice", type: "number" },
  reviewrating: { apiKey: "reviewRating", type: "number" },
  cancellationpolicyids: { apiKey: "cancellationPolicyIds", type: "numberArray" },
  cancellationpolicyid: { apiKey: "cancellationPolicyIds", type: "numberArray" },
  includestypeids: { apiKey: "includesTypeIds", type: "numberArray" },
  includetypeids: { apiKey: "includesTypeIds", type: "numberArray" },
  includesTypeIds: { apiKey: "includesTypeIds", type: "numberArray" },
  instantconfirm: { apiKey: "instantConfirm", type: "string" },
  sortby: { apiKey: "sortBy", type: "string" },
};

function parseStringArray(raw) {
  if (raw == null || raw === "") return [];
  const values = Array.isArray(raw) ? raw : String(raw).split(",");
  return values.map((item) => String(item).trim()).filter(Boolean);
}

function parseFilterQueryValue(raw, type) {
  if (raw == null || raw === "") return undefined;

  switch (type) {
    case "number": {
      const parsed = Number(parseStringValue(raw));
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    case "numberArray": {
      const values = parseIdList(raw);
      return values.length ? values : undefined;
    }
    case "stringArray": {
      const values = parseStringArray(raw);
      return values.length ? values : undefined;
    }
    default: {
      const value = parseStringValue(raw);
      return value || undefined;
    }
  }
}

function mergeFilterPayloadValue(payload, apiKey, value, type) {
  if (value == null) return;

  if (type === "numberArray" || type === "stringArray") {
    const current = Array.isArray(payload[apiKey]) ? payload[apiKey] : [];
    payload[apiKey] = [...new Set([...current, ...value])];
    return;
  }

  payload[apiKey] = value;
}

export function buildActivityFilterPayload(query = {}) {
  const payload = {};

  Object.entries(query || {}).forEach(([rawKey, rawValue]) => {
    const config = FILTER_QUERY_MAP[normalizeQueryKey(rawKey)];
    if (!config) return;

    const parsed = parseFilterQueryValue(rawValue, config.type);
    mergeFilterPayloadValue(payload, config.apiKey, parsed, config.type);
  });

  return compactActivityListPayload(payload);
}

export function getUrlPageTitle(urlParams = {}) {
  if (urlParams.cityName) return urlParams.cityName;
  if (urlParams.countryName) return urlParams.countryName;
  if (urlParams.citySlug) return String(urlParams.citySlug).replace(/-/g, " ");
  if (urlParams.countrySlug) return String(urlParams.countrySlug).replace(/-/g, " ");
  return "";
}

export function getUrlFilterDefaults(urlParams = {}) {
  return {
    activityTypeIds: urlParams.activityTypeIds || [],
    subTypeIds: urlParams.subTypeIds || [],
    cityIds: urlParams.cityIds || (urlParams.cityId ? [urlParams.cityId] : []),
    activityIds: urlParams.activityIds || [],
  };
}

function asOptions(list, labelKey = "label", idKey = "id") {
  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => {
      if (item == null) return null;

      if (typeof item === "string") {
        return { id: item, label: item, value: item, raw: item };
      }

      const id = item[idKey] ?? item.id ?? item.value ?? item.slug ?? index;
      const label =
        item[labelKey] ??
        item.label ??
        item.title ??
        item.name ??
        String(id);

      return {
        id,
        label,
        value: item.value ?? id,
        icon: item.icon || "",
        slug: item.slug || "",
        activityCount: item.activityCount ?? null,
        raw: item,
      };
    })
    .filter(Boolean);
}

function asCityOptions(list = []) {
  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      const id = item.cityId ?? item.id ?? index;
      return {
        id,
        label: item.label ?? item.cityName ?? item.name ?? String(id),
        value: id,
        slug: item.slug || "",
        activityCount: item.activityCount ?? null,
        raw: item,
      };
    })
    .filter(Boolean);
}

function readBudget(source = {}, fallbackCurrency = "") {
  return {
    min: Number(source.minPrice ?? source.min ?? 0) || 0,
    max: Number(source.maxPrice ?? source.max ?? 0) || 0,
    currency: source.currency || fallbackCurrency || "",
  };
}

export function normalizeActivityFilters(response) {
  const data = response?.data || response || {};
  const budget = readBudget(data.budget || data.price || data.priceRange, data.currency);

  return {
    originCountry: data.originCountry || "",
    currency: data.currency || budget.currency || "",
    totalActivities: Number(data.totalActivities) || 0,
    budget,
    price: { min: budget.min, max: budget.max, currency: budget.currency },
    activityTypes: asOptions(data.activityTypes || data.activityType),
    subTypes: asOptions(data.subTypes || data.subType),
    cities: asCityOptions(data.cities || data.cityList),
    countries: asOptions(data.countries, "label", "countryId"),
    timeOfDay: asOptions(data.timeOfDay, "label", "value"),
    durations: asOptions(data.durations, "label", "value"),
    reviewRatings: asOptions(data.reviewRatings || data.reviewRating, "label", "value"),
    cancellationPolicies: asOptions(
      data.cancellationPolicies || data.cancellationPolicy
    ),
    includes: asOptions(
      data.includes || data.includesTypeIds || data.popularFilters
    ),
    instantConfirm: asOptions(data.instantConfirm, "label", "value"),
    sortOptions: asOptions(data.sortBy || data.sortOptions, "label", "value"),
    title:
      data.pageTitle ||
      data.destinationName ||
      data.countryName ||
      data.cityName ||
      "",
  };
}

export function buildActivityListPayload({
  urlParams = {},
  filterState = {},
  pageIndex = 1,
  pageSize = 10,
  sortBy = "",
} = {}) {
  const payload = {
    ...pickUrlApiParams(urlParams),
    pageIndex,
    pageSize,
  };

  const mergeArrayParam = (key, values = []) => {
    if (!values.length) return;
    payload[key] = [...new Set([...(payload[key] || []), ...values])];
  };

  mergeArrayParam("activityTypeIds", filterState.activityTypeIds);
  mergeArrayParam("subTypeIds", filterState.subTypeIds);
  mergeArrayParam("cityIds", filterState.cityIds);
  mergeArrayParam("activityIds", filterState.activityIds);
  mergeArrayParam("durations", filterState.durations);
  mergeArrayParam("timeOfDay", filterState.timeOfDay);
  mergeArrayParam("cancellationPolicyIds", filterState.cancellationPolicyIds);
  mergeArrayParam("includesTypeIds", filterState.includesTypeIds);

  if (filterState.minPrice > 0) {
    payload.minPrice = filterState.minPrice;
  }
  if (filterState.maxPrice > 0) {
    payload.maxPrice = filterState.maxPrice;
  }
  if (filterState.reviewRating > 0) {
    payload.reviewRating = filterState.reviewRating;
  }
  if (filterState.instantConfirm) {
    payload.instantConfirm = filterState.instantConfirm;
  }

  if (sortBy && sortBy !== "recommended") {
    payload.sortBy = sortBy;
  }

  return compactActivityListPayload(payload);
}

export function buildFilterState({
  activityTypeIds = [],
  subTypeIds = [],
  cityIds = [],
  activityIds = [],
  cancellationPolicyIds = [],
  includesTypeIds = [],
  durations = [],
  timeOfDay = [],
  budgetRange = [],
  reviewRating = 0,
  instantConfirm = "",
  filterBounds = {},
}) {
  const [budgetMin, budgetMax] = budgetRange;

  const state = {
    activityTypeIds,
    subTypeIds,
    cityIds,
    activityIds,
    cancellationPolicyIds,
    includesTypeIds,
    reviewRating,
  };

  if (durations.length) state.durations = durations;
  if (timeOfDay.length) state.timeOfDay = timeOfDay;

  if (budgetMax > 0 && budgetMax !== filterBounds.budgetMax) {
    state.maxPrice = budgetMax;
  }
  if (budgetMin > 0 && budgetMin !== filterBounds.budgetMin) {
    state.minPrice = budgetMin;
  }

  if (instantConfirm) state.instantConfirm = instantConfirm;

  return state;
}

export function buildReviewBands(reviewRatings = []) {
  if (!reviewRatings.length) return [];

  return reviewRatings.map((item) => ({
    id: item.id,
    label: item.label,
    min: Number(item.raw?.value ?? item.value ?? item.id) || 0,
  }));
}
